/**
 * Customer disposal fees — data hooks (#52, DEC-358). The API is the gate (reads
 * VIEW_INVOICES, writes MANAGE_DISPOSAL_PRICES); these make no trust decisions.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { authedFetch, useApiOpts, useApiQuery, type WebApiOpts } from '@lodex-pro/web-shared';
import type {
  Customer,
  CustomerFeesResponse,
  HistoryRow,
  ImportRow,
  ImportSource,
  PlaceKind,
  PreviewCounts,
  PreviewResponse,
  SetFeeBody,
  SiteDetailResponse,
  SitesResponse,
} from './customer-disposal-fees';

const BASE = '/v1/customer-disposal-fees';
export const FEES_KEY = ['billing', 'customer-disposal-fees'] as const;

export function useFeeSites(): ReturnType<typeof useApiQuery<SitesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<SitesResponse>(opts, `${BASE}/sites`, {
    queryKey: [...FEES_KEY, 'sites'],
    staleTime: 30_000,
  });
}

export function useFeeSite(
  site: { kind: PlaceKind; id: string } | null,
): ReturnType<typeof useApiQuery<SiteDetailResponse>> {
  const opts = useApiOpts();
  return useApiQuery<SiteDetailResponse>(
    opts,
    site === null ? `${BASE}/sites` : `${BASE}/sites/${site.kind}/${site.id}`,
    {
      queryKey: [...FEES_KEY, 'site', site?.kind ?? '', site?.id ?? ''],
      enabled: site !== null,
      staleTime: 30_000,
    },
  );
}

export function useFeeCustomers(): ReturnType<typeof useApiQuery<{ customers: Customer[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ customers: Customer[] }>(opts, `${BASE}/customers`, {
    queryKey: [...FEES_KEY, 'customers'],
    staleTime: 60_000,
  });
}

export function useCustomerFees(
  customerId: string | null,
): ReturnType<typeof useApiQuery<CustomerFeesResponse>> {
  const opts = useApiOpts();
  return useApiQuery<CustomerFeesResponse>(opts, `${BASE}/customers/${customerId ?? ''}`, {
    queryKey: [...FEES_KEY, 'customer', customerId ?? ''],
    enabled: customerId !== null,
    staleTime: 30_000,
  });
}

export interface HistoryFilter {
  readonly haulerOperatorId?: string;
  readonly kind?: PlaceKind;
  readonly id?: string;
}

export function useFeeHistory(
  filter: HistoryFilter | null,
): ReturnType<typeof useApiQuery<{ rows: HistoryRow[]; truncated: boolean }>> {
  const opts = useApiOpts();
  const qs = new URLSearchParams();
  if (filter?.haulerOperatorId !== undefined) qs.set('haulerOperatorId', filter.haulerOperatorId);
  if (filter?.kind !== undefined && filter.id !== undefined) {
    qs.set('kind', filter.kind);
    qs.set('id', filter.id);
  }
  return useApiQuery<{ rows: HistoryRow[]; truncated: boolean }>(
    opts,
    `${BASE}/history?${qs.toString()}`,
    { queryKey: [...FEES_KEY, 'history', qs.toString()], enabled: filter !== null },
  );
}

/** Every fee query, after any write: the views all derive from the same rows. */
function useInvalidateFees(): () => void {
  const qc = useQueryClient();
  return (): void => {
    void qc.invalidateQueries({ queryKey: FEES_KEY });
  };
}

/** The first field reason of an API refusal, else its message, else the status. */
export async function refusalMessage(res: Response): Promise<string> {
  if (res.status === 403) return 'Your role cannot change customer disposal fees.';
  if (res.status === 413) return 'That file is over the 2 MB limit.';
  const fallback = `Failed (${String(res.status)}).`;
  try {
    const body = (await res.json()) as {
      error?: { message?: string; fields?: { reason?: string }[] };
    };
    return body.error?.fields?.[0]?.reason ?? body.error?.message ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * A write to the fee API. Unlike useApiMutation this keeps the API's own reason for a
 * refusal (VALIDATION_FAILED carries it per field), because here the reason IS the
 * message: "Prices changed since this preview", "already a change on that date".
 */
async function feeRequest<T>(opts: WebApiOpts, path: string, body: unknown): Promise<T> {
  const res = await authedFetch(opts, path, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) throw new Error(await refusalMessage(res));
  return (await res.json()) as T;
}

export function useSetFee(
  onDone: () => void,
): UseMutationResult<{ id: string }, Error, SetFeeBody> {
  const opts = useApiOpts();
  const invalidate = useInvalidateFees();
  return useMutation<{ id: string }, Error, SetFeeBody>({
    mutationFn: (body) => feeRequest(opts, BASE, body),
    onSuccess: () => {
      invalidate();
      onDone();
    },
  });
}

export interface PreviewRequest {
  readonly source: ImportSource;
  readonly defaultEffectiveFrom: string;
  readonly rows: readonly ImportRow[];
}

export type ApplyRequest = PreviewRequest & {
  readonly issuedAt: string;
  readonly previewToken: string;
};

export interface ApplyResult {
  readonly importId: string;
  readonly counts: PreviewCounts;
}

export function usePreviewImport(): UseMutationResult<PreviewResponse, Error, PreviewRequest> {
  const opts = useApiOpts();
  return useMutation<PreviewResponse, Error, PreviewRequest>({
    mutationFn: (body) => feeRequest(opts, `${BASE}/import/preview`, body),
  });
}

export function useApplyImport(
  onDone: (r: ApplyResult) => void,
): UseMutationResult<ApplyResult, Error, ApplyRequest> {
  const opts = useApiOpts();
  const invalidate = useInvalidateFees();
  return useMutation<ApplyResult, Error, ApplyRequest>({
    mutationFn: (body) => feeRequest(opts, `${BASE}/import/apply`, body),
    onSuccess: (r) => {
      invalidate();
      onDone(r);
    },
  });
}

export interface ParsedFile {
  readonly fileName: string;
  readonly fileSha256: string;
  readonly rows: ImportRow[];
}

/** Upload a CSV/XLSX for parsing; throws Error with the API's reason on refusal. */
export async function parseFeeFile(opts: WebApiOpts, file: File): Promise<ParsedFile> {
  const form = new FormData();
  form.append('file', file, file.name);
  const res = await authedFetch(opts, `${BASE}/import/parse`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(await refusalMessage(res));
  return (await res.json()) as ParsedFile;
}

/** Download the long-list export. */
export async function downloadFees(
  opts: WebApiOpts,
  args: { format: 'csv' | 'xlsx'; every: boolean },
): Promise<void> {
  const res = await authedFetch(
    opts,
    `${BASE}/export?format=${args.format}&every=${String(args.every)}`,
  );
  if (!res.ok) throw new Error(`Export failed (${String(res.status)}).`);
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const name =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? `customer-disposal-fees.${args.format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
